import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";

import { Anchor } from "@components/Anchor";
import { TextInput } from "@components/Input";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Caption } from "@components/Typography";
import { VERIFIED_TERMS_OF_USE_URL } from "@constants";
import { useEntryIntakeStore } from "@entry";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useErrorBanner, useOnboarding, useSetUserIdentity } from "@hooks";
import { i18n, Translations } from "@i18n";
import { constants } from "@layout";
import { validationSchema } from "./content";
import { EmailFormValues } from "./types";

export const EmailRoute = () => {
  const {
    register,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
    formState: { errors, isValid },
  } = useForm<EmailFormValues>({
    defaultValues: {
      email: useEntryIntakeStore.getState().intake.email,
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const { resetToNext } = useOnboarding();

  const [setUserIdentity, { loading }] = useSetUserIdentity();

  const { showErrorBanner } = useErrorBanner();

  const handleSubmit = async ({ email }: EmailFormValues) => {
    try {
      await setUserIdentity({
        variables: {
          email,
        },
      });
      resetToNext();
    } catch {
      showErrorBanner();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.EmailRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.EmailRoutePrimaryCTA),
        onClick: handleFormSubmit(handleSubmit),
        disabled: !isValid,
        loading,
      },
    },
  };

  const emailController = register("email");

  const onChangeEmailText = (value: string) => {
    setValue("email", value, constants.form);
  };

  return (
    <OnboardingLayout {...props}>
      <TextInput
        {...emailController}
        label={i18n.t(Translations.EmailRouteInputLabel)}
        value={watch("email")}
        onChangeText={onChangeEmailText}
        error={errors.email?.message}
        placeholder={i18n.t(Translations.EmailRouteInputPlaceholder)}
      />
      <Spacing size={SpacingSizes.M} />

      <Caption style={{ textAlign: "center" }}>
        {i18n.t(Translations.VerifiedFooter1)}
        <Anchor href={VERIFIED_TERMS_OF_USE_URL}>
          {i18n.t(Translations.VerifiedFooterHighlight)}
        </Anchor>
        {i18n.t(Translations.VerifiedFooter2)}
      </Caption>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
